/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import { StatusStats, SurveyStats, UserWithDelegation } from '../../api/initiative-stats';
import { DateRangeType } from '../../components/period-dropdown/DateRangePicker';
import { getFilteredUtrIds, getTaggedUtrIds } from '../../components/survey/utils/getDisableUtrs';
import { TagStatus } from '../../constants/status';
import { Tag } from '../../types/metricGroup';
import { UniversalTrackerBlueprintMin } from '../../types/universalTracker';
import { DATE, getUTCEndOf, getUTCStartOf, setFormat } from '../../utils/date';
import { hasFramework, hasStandard } from '../../utils/universalTracker';

// Convert to percentage
export function toPercentageProgress(status: SurveyStats['status']): SurveyStats['status'] {

  const total = status.created + status.updated + status.rejected + status.verified;
  if (total === 0) {
    return status;
  }

  return {
    created: Math.round(status.created / total * 100),
    updated: Math.round(status.updated / total * 100),
    rejected: Math.round(status.rejected / total * 100),
    verified: Math.round(status.verified / total * 100)
  }
}

export const getUTCDateRange = (dateRange: DateRangeType) => {
  return {
    ...(dateRange.startDate && {startDate: getUTCStartOf('day', setFormat(DATE.DATE_PICKER, dateRange.startDate))}),
    ...(dateRange.endDate && { endDate: getUTCEndOf('day', setFormat(DATE.DATE_PICKER, dateRange.endDate)) }),
  };
};

export const getTotalDelegationCount = (status?: StatusStats) => {
  return status ? status.created + status.updated + status.verified + status.rejected : 0;
}

export const getUserDelegationStats = (user: UserWithDelegation) => ({
  contributor: getTotalDelegationCount(user.contributor),
  updated: user.contributor?.updated ?? 0,
  verifier: getTotalDelegationCount(user.verifier) ?? 0,
  verified: user.verifier?.verified ?? 0,
});

export const PAGE_SIZE = 10;

export const getFilteredUtrIdsByTag = ({
  utrIds,
  tags,
  values,
}: {
  utrIds: string[];
  tags: Tag[];
  values: string[];
}) => {
  if (values.includes(TagStatus.NoTag) && values.length > 1) {
    return [];
  }

  if (!values.includes(TagStatus.NoTag)) {
    return getFilteredUtrIds(values, tags);
  }

  if (values.includes(TagStatus.NoTag) && values.length === 1) {
    const taggedUtrIds = getTaggedUtrIds(tags);
    return utrIds.filter((id) => !taggedUtrIds.includes(id));
  }

  return [];
};

export const isMatchSelectedPacks = (
  utr: UniversalTrackerBlueprintMin,
  packs: string[]
) => {
  if (packs.length === 0) {
    return true;
  }
  // utr must match all of selected packs
  for (const pack of packs) {
    if (!(hasStandard(utr, pack) || hasFramework(utr, pack))) {
      return false;
    }
  }
  return true;
};
